import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Search, X, Check, AlertCircle, FileText, Building, Calendar } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { debitNotesAPI, partiesAPI, itemsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';

const CreateDebitNotePage = () => {
  const { businessId, noteId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const isEditing = !!noteId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [newlyAddedIndex, setNewlyAddedIndex] = useState(null);
  const supplierSearchRef = useRef(null);
  const itemSearchRef = useRef(null);

  const [formData, setFormData] = useState({
    supplier_id: '',
    debit_note_date: new Date().toISOString().split('T')[0],
    reason: '',
    reason_description: '',
    reference_invoice_number: '',
    items: [],
    notes: '',
  });

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchSuppliers();
      fetchProducts();
      fetchReasons();
      if (isEditing) fetchNote();
    }
  }, [bid, noteId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (supplierSearchRef.current && !supplierSearchRef.current.contains(e.target)) {
        setShowSupplierDropdown(false);
      }
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNote = async () => {
    try {
      setLoading(true);
      const response = await debitNotesAPI.get(noteId);
      const data = response.data.debit_note || response.data;

      if (data.status && data.status !== 'draft' && data.status !== 'DRAFT') {
        toast.error('Only draft debit notes can be edited');
        navigate(`/business/${bid}/debit-notes/${noteId}`);
        return;
      }

      setFormData(prev => ({
        ...prev,
        supplier_id: data.supplier_id || data.party_id || '',
        debit_note_date: data.note_date || data.debit_note_date || data.date || prev.debit_note_date,
        reason: data.reason || '',
        reason_description: data.reason_description || '',
        reference_invoice_number: data.reference_invoice_number || '',
        items: data.items || [],
        notes: data.notes || '',
      }));

      const supName = data.supplier_name || data.party_name;
      const supId = data.supplier_id || data.party_id;
      const sup = suppliers.find(s => (s.party_id || s.id) === supId);
      if (sup) { setSelectedSupplier(sup); setSupplierSearch(sup.name); }
      else if (supName) { setSupplierSearch(supName); }
    } catch (err) { toast.error(getApiErrorMessage(err, 'Error loading debit note')); }
    finally { setLoading(false); }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await partiesAPI.list({ type: 'supplier' });
      const data = response.data;
      setSuppliers(data.parties || data || []);
    } catch (err) { toast.error(getApiErrorMessage(err, 'Failed to load suppliers')); }
  };

  const fetchProducts = async () => {
    try {
      const response = await itemsAPI.list();
      const data = response.data;
      setProducts(data.data || data || []);
    } catch (err) { toast.error(getApiErrorMessage(err, 'Failed to load products')); }
  };

  const fetchReasons = async () => {
    try {
      const response = await debitNotesAPI.getReasons();
      const data = response.data;
      setReasons(Array.isArray(data) ? data : data.reasons || []);
    } catch (err) { toast.error(getApiErrorMessage(err, 'Failed to load reasons')); }
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setShowSupplierDropdown(false);
    setFormData(prev => ({ ...prev, supplier_id: supplier.party_id || supplier.id }));
  };

  const addItem = (product = null) => {
    if (product) {
      const existingIndex = formData.items.findIndex(item => item.item_id === product.id);
      if (existingIndex >= 0) {
        updateItem(existingIndex, 'quantity', (formData.items[existingIndex].quantity || 1) + 1);
        return;
      }
      const rate = product.purchase_price || product.cost_price || product.selling_price || 0;
      const taxRate = product.tax_rate || 0;
      const taxAmt = rate * (taxRate / 100);

      setFormData(prev => {
        const newIndex = prev.items.length;
        setNewlyAddedIndex(newIndex);
        setTimeout(() => setNewlyAddedIndex(null), 400);
        return {
          ...prev,
          items: [...prev.items, {
            item_id: product.id, item_name: product.name,
            hsn_code: product.hsn_code || '', quantity: 1, unit: product.unit || 'Nos',
            rate, tax_rate: taxRate,
            tax_amount: Math.round(taxAmt * 100) / 100,
            amount: Math.round((rate + taxAmt) * 100) / 100,
          }],
        };
      });
    } else {
      setFormData(prev => {
        const newIndex = prev.items.length;
        setNewlyAddedIndex(newIndex);
        setTimeout(() => setNewlyAddedIndex(null), 400);
        return {
          ...prev,
          items: [...prev.items, {
            item_id: '', item_name: '', hsn_code: '', quantity: 1, unit: 'Nos',
            rate: 0, tax_rate: 0, tax_amount: 0, amount: 0,
          }],
        };
      });
    }
    setItemSearch('');
    setShowItemDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === 'item_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].item_name = product.name;
          newItems[index].hsn_code = product.hsn_code || '';
          newItems[index].rate = product.purchase_price || product.cost_price || product.selling_price || 0;
          newItems[index].unit = product.unit || 'Nos';
          newItems[index].tax_rate = product.tax_rate || 0;
        }
      }

      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;
      const lineTotal = qty * rate;
      const taxAmt = lineTotal * (taxRate / 100);

      newItems[index].tax_amount = Math.round(taxAmt * 100) / 100;
      newItems[index].amount = Math.round((lineTotal + taxAmt) * 100) / 100;

      return { ...prev, items: newItems };
    });
  };

  // Totals
  const subtotal = formData.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.rate || 0)), 0);
  const taxTotal = formData.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const grandTotal = subtotal + taxTotal;

  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!formData.supplier_id) errors.supplier = 'Supplier is required';
    if (!formData.reason) errors.reason = 'Reason is required';
    if (formData.items.length === 0) errors.items = 'At least one item is required';
    else {
      formData.items.forEach((item, index) => {
        if (!item.item_id) errors[`item_${index}_product`] = 'Select a product';
        if (!item.quantity || item.quantity <= 0) errors[`item_${index}_quantity`] = 'Quantity must be > 0';
      });
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setValidationErrors({});
    if (!validateForm()) return;

    setSaving(true);
    setError('');
    try {
      const payload = {
        supplier_id: formData.supplier_id,
        debit_note_date: formData.debit_note_date,
        reference_invoice_id: null,
        reference_invoice_number: formData.reference_invoice_number || null,
        reason: formData.reason,
        reason_description: formData.reason_description || null,
        items: formData.items,
        subtotal,
        tax_amount: taxTotal,
        total_amount: grandTotal,
        notes: formData.notes || null,
      };

      let response;
      if (isEditing) {
        response = await debitNotesAPI.update(noteId, payload);
      } else {
        response = await debitNotesAPI.create(payload);
      }
      const data = response.data;

      toast.success(isEditing ? 'Debit note updated!' : 'Debit note created!');
      const resultId = data.id || data.debit_note?.id || noteId;
      navigate(`/business/${bid}/debit-notes/${resultId}`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Error saving debit note'));
    } finally { setSaving(false); }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    s.mobile?.includes(supplierSearch) ||
    s.email?.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F0F2F5]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${bid}/debit-notes`)}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">
                  {isEditing ? 'Edit Debit Note' : 'New Debit Note'}
                </h1>
                <p className="text-[12px] text-slate-500 mt-0.5">Record debit adjustments against suppliers</p>
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
            >
              <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">

            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-12 lg:col-span-8 space-y-4">

              {/* Error / Success */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                  <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {success && (
                <div className="mb-4 p-4 bg-teal-50 border border-teal-200 rounded-xl flex items-center gap-3">
                  <Check className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  <p className="text-sm text-teal-700">{success}</p>
                </div>
              )}

              {/* SUPPLIER DETAILS */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Supplier Details</h2>
                    <p className="text-[11px] text-slate-500">Search and select supplier</p>
                  </div>
                </div>

                <div className="relative" ref={supplierSearchRef}>
                  <label className={`block text-[12px] font-medium mb-2 ${validationErrors.supplier ? 'text-red-500' : 'text-slate-600'}`}>
                    Supplier {validationErrors.supplier && <span className="text-red-500 ml-1">* Required</span>}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text" value={supplierSearch}
                      onChange={(e) => { setSupplierSearch(e.target.value); setShowSupplierDropdown(true); }}
                      onFocus={() => setShowSupplierDropdown(true)}
                      className={`w-full pl-11 pr-4 py-3 bg-slate-50 border-2 rounded-xl text-[14px] focus:bg-white focus:ring-0 focus:outline-none transition-all duration-200 ${
                        validationErrors.supplier
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-slate-200 focus:border-teal-500'
                      }`}
                      placeholder="Search by name, phone, or email..."
                    />
                  </div>

                  {showSupplierDropdown && filteredSuppliers.length > 0 && (
                    <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {filteredSuppliers.slice(0, 8).map((supplier, idx) => {
                        const contact = [supplier.mobile, supplier.email].filter(Boolean).join(' -- ');
                        return (
                          <div key={supplier.party_id || supplier.id}
                            onClick={() => handleSupplierSelect(supplier)}
                            className={`px-4 py-3 hover:bg-teal-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{supplier.name}</p>
                              {contact && <p className="text-[12px] text-slate-500 shrink-0 truncate">{contact}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedSupplier && (() => {
                  const selContact = [selectedSupplier.mobile, selectedSupplier.email].filter(Boolean).join(' -- ');
                  return (
                    <div className="mt-4 p-3 bg-teal-50/50 rounded-lg border border-teal-100">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <p className="text-[13px] font-medium text-slate-800 truncate">{selectedSupplier.name}</p>
                          {selContact && <p className="text-[11px] text-slate-500 truncate">{selContact}</p>}
                        </div>
                        <button onClick={() => { setSelectedSupplier(null); setSupplierSearch(''); setFormData(prev => ({ ...prev, supplier_id: '' })); }}
                          className="text-slate-400 hover:text-slate-600 shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* ITEMS */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Items</h2>
                      <p className="text-[11px] text-slate-500">{formData.items.length} item{formData.items.length !== 1 ? 's' : ''} added</p>
                    </div>
                  </div>
                </div>

                {/* Item search bar ABOVE the table */}
                <div className="flex items-center gap-3 mb-5 p-3 bg-slate-50 rounded-xl">
                  <div className="flex-1 relative" ref={itemSearchRef}>
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={itemSearch}
                      onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                      onFocus={() => setShowItemDropdown(true)}
                      className="w-full pl-11 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-[14px] focus:border-teal-500 focus:ring-0 focus:outline-none transition-all duration-200"
                      placeholder="Search items by name or code..."
                    />
                    {showItemDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                        {filteredProducts.slice(0, 8).map((product, idx) => (
                          <div key={product.id} onClick={() => addItem(product)}
                            className={`px-4 py-3 hover:bg-teal-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{product.name}</p>
                              {product.code && <p className="text-[12px] text-slate-500 shrink-0">{product.code}</p>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > 8 && (
                          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 text-center">
                            Showing 8 of {filteredProducts.length} -- type to narrow
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button onClick={() => addItem()}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]">
                    <Plus className="w-4 h-4" /> Add Line
                  </button>
                </div>

                {/* Items Table */}
                {formData.items.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                      <FileText className="w-7 h-7 text-slate-400" />
                    </div>
                    <p className="text-[14px] font-medium text-slate-600">No items added yet</p>
                    <p className="text-[12px] text-slate-400 mt-1">Search above or click "Add Line" to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">#</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Item Name</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">HSN</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Unit</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Rate</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Tax %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Tax Amt</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Amount</th>
                          <th className="px-4 py-3 w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {formData.items.map((item, index) => (
                          <tr key={`item-${index}-${item.item_id || 'new'}`}
                              className={`hover:bg-slate-50/80 transition-colors duration-150 ${
                                index === newlyAddedIndex ? 'animate-lineItemSlideIn animate-rowHighlight' : ''
                              }`}>
                            <td className="px-4 py-3 text-[13px] text-slate-500 font-medium">{index + 1}</td>
                            <td className="px-4 py-3">
                              <select value={item.item_id} onChange={(e) => updateItem(index, 'item_id', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500 focus:bg-white transition-all">
                                <option value="">Select Item</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <input type="text" value={item.hsn_code || ''} onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                                className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-teal-500 focus:bg-white transition-all" placeholder="--" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-teal-500 focus:bg-white transition-all" min="0" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="text" value={item.unit} onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-teal-500 focus:bg-white transition-all" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.rate} onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-teal-500 focus:bg-white transition-all" min="0" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-teal-500 focus:bg-white transition-all" min="0" placeholder="%" />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-[13px] text-slate-600">
                                {(item.tax_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-[14px] font-semibold text-teal-600">
                                {(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button onClick={() => removeItem(index)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 active:scale-95">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* NOTES & REASON DESCRIPTION */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Notes & Description</h2>
                    <p className="text-[11px] text-slate-500">Additional information</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Notes</label>
                    <textarea value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-slate-400 focus:outline-none resize-none transition-all duration-200"
                      rows={3} placeholder="Add notes..."
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Reason Description</label>
                    <textarea value={formData.reason_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason_description: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-slate-400 focus:outline-none resize-none transition-all duration-200"
                      rows={3} placeholder="Describe the reason in detail..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols */}
            <div className="col-span-12 lg:col-span-4 space-y-4">

              {/* NOTE DETAILS */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Note Details</h2>
                    <p className="text-[11px] text-slate-500">Configure debit note settings</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Debit Note Date</label>
                    <input type="date" value={formData.debit_note_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, debit_note_date: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-[12px] font-medium mb-2 ${validationErrors.reason ? 'text-red-500' : 'text-slate-600'}`}>
                      Reason {validationErrors.reason && <span className="text-red-500 ml-1">* Required</span>}
                    </label>
                    <select value={formData.reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                      className={`w-full px-4 py-2.5 bg-white border rounded-lg text-[13px] focus:outline-none transition-all duration-200 ${
                        validationErrors.reason
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-slate-200 focus:border-teal-500'
                      }`}>
                      <option value="">Select reason...</option>
                      {reasons.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Reference Invoice Number</label>
                    <input type="text" value={formData.reference_invoice_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference_invoice_number: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500"
                      placeholder="Optional invoice reference"
                    />
                  </div>
                </div>
              </div>

              {/* SUMMARY - Teal Gradient */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-5 shadow-sm border border-teal-200/60">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Summary</h2>
                    <p className="text-[11px] text-slate-500">Final amounts</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-teal-600 font-medium">+ {taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>

                  <div className="border-t border-teal-200 pt-4 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                      <span className="text-[20px] font-bold text-teal-600">
                        {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDebitNotePage;
